const nestedErrorKeys = ['message', 'error', 'err', 'detail', 'details'] as const;

function getStringValue(value: unknown): string | null {
  if (typeof value !== 'string') {
    return null;
  }

  const trimmedValue = value.trim();
  return trimmedValue === '' ? null : trimmedValue;
}

function isUnhelpfulErrorMessage(message: string | null): boolean {
  if (!message) {
    return true;
  }

  return message === '[object Object]' || message === 'Error' || message === 'Unknown error';
}

function extractNestedErrorMessage(error: unknown, visited = new Set<object>()): string | null {
  const directString = getStringValue(error);
  if (directString) {
    return directString;
  }

  if (error instanceof Error) {
    const errorMessage = getStringValue(error.message);
    if (!isUnhelpfulErrorMessage(errorMessage)) {
      return errorMessage;
    }

    const nestedErrorMessage = extractNestedErrorMessage(error.cause, visited);
    if (nestedErrorMessage) {
      return nestedErrorMessage;
    }

    const ownPropertiesMessage = extractNestedErrorMessage(
      Object.fromEntries(
        Object.entries(error as Error & Record<string, unknown>).filter(([key]) => key !== 'message' && key !== 'name')
      ),
      visited
    );
    if (ownPropertiesMessage) {
      return ownPropertiesMessage;
    }

    return errorMessage ?? getStringValue(error.name) ?? null;
  }

  if (!error || typeof error !== 'object') {
    return null;
  }

  if (visited.has(error)) {
    return null;
  }
  visited.add(error);

  const errorRecord = error as Record<string, unknown>;

  if ('data' in errorRecord) {
    const nestedDataMessage = extractNestedErrorMessage(errorRecord.data, visited);
    if (nestedDataMessage) {
      return nestedDataMessage;
    }
  }

  for (const key of nestedErrorKeys) {
    if (key in errorRecord) {
      const nestedMessage = extractNestedErrorMessage(errorRecord[key], visited);
      if (nestedMessage) {
        return nestedMessage;
      }
    }
  }

  const status = errorRecord.status;
  const statusText = getStringValue(errorRecord.statusText);
  if ((typeof status === 'number' || typeof status === 'string') && statusText) {
    return `HTTP ${status}: ${statusText}`;
  }

  if (Array.isArray(error)) {
    const nestedMessages = error
      .map((item) => extractNestedErrorMessage(item, visited))
      .filter((message): message is string => Boolean(message));

    if (nestedMessages.length > 0) {
      return nestedMessages.join('; ');
    }
  }

  return null;
}

/**
 * Extract error message from backend errors (handles various error formats).
 *
 * @internal SDK-private helper for useInlineAssistant. Not part of the public
 * `@grafana/assistant` surface — do not re-export from `index.ts`.
 */
export function extractErrorMessage(error: unknown): string {
  const extractedMessage = extractNestedErrorMessage(error);
  if (extractedMessage) {
    return extractedMessage;
  }

  // Objects that expose no message are not serialized: fetch-like errors carry
  // the request URL, headers and body on `config`, which would end up in the
  // messages this helper feeds back to callers.
  if (error && typeof error === 'object') {
    return 'Unknown error';
  }

  if (error == null) {
    return 'Unknown error';
  }

  return String(error);
}
